/**
 * use-project-group-level
 *
 * Loads one page of one sibling set of the server-aggregated group tree: the groups at
 * `depth` sitting under `parentPath`. Every sibling set on screen owns an instance, so
 * grouping 50k projects by client pages through the ~5k clients instead of rendering
 * them all — mirroring how `ServerLeafGrid` pages a leaf group's project rows.
 *
 * Counts and subtotals come from the server's aggregate for that level, so a group's
 * figures cover all of its descendants even while none of its children are loaded.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchProjectGroupedTree, type ProjectFilterParams } from '@/lib/data-service';
import {
  mapServerGroupLevel,
  type GroupById,
  type GroupByRefData,
  type GroupPathSegment,
  type ProjectGroup,
} from '@/lib/project-group-by-utils';
import type { GroupSubtotals } from '@/lib/project-group-by-utils';
import type { PaginationMetadata } from '@/types/pagination';
import { useErrorHandler } from '@/hooks/use-error-handler';

export interface UseProjectGroupLevelOptions {
  /** The full ordered group-by list; `depth` selects the level within it. */
  groupBy: GroupById[];
  depth: number;
  /** Raw key path of this sibling set's ancestors, outermost first. */
  parentPath: GroupPathSegment[];
  /** Reference data for resolving status/type/health labels locally. */
  referenceData: GroupByRefData;
  /** List-level filters (search / status / health / scope) that scope the whole tree. */
  filters?: ProjectFilterParams;
  initialPageSize: number;
  /** False while the parent is collapsed, so children are only fetched on expand. */
  enabled: boolean;
}

export interface UseProjectGroupLevelResult {
  groups: ProjectGroup[];
  /** Paging over this sibling set — `totalItems` counts groups, not projects. */
  pagination?: PaginationMetadata;
  /** Count and subtotals of the whole sibling set, across every page of it. */
  totals?: GroupSubtotals;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

export function useProjectGroupLevel({
  groupBy,
  depth,
  parentPath,
  referenceData,
  filters,
  initialPageSize,
  enabled,
}: UseProjectGroupLevelOptions): UseProjectGroupLevelResult {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [nodes, setNodes] = useState<ProjectGroup[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | undefined>(undefined);
  const [totals, setTotals] = useState<GroupSubtotals | undefined>(undefined);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const { handleApiError } = useErrorHandler({ useToast: true });

  const groupByKey = groupBy.join(',');
  const parentKeys = useMemo(() => parentPath.map((segment) => segment.key), [parentPath]);
  // Identifies the sibling set and the scope it sits in; any change restarts paging.
  const requestKey = useMemo(
    () => JSON.stringify([groupByKey, depth, parentKeys, filters ?? null]),
    [groupByKey, depth, parentKeys, filters],
  );

  useEffect(() => {
    setPage(1);
  }, [requestKey]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);

    fetchProjectGroupedTree(groupBy, filters, { depth, parentPath: parentKeys, page, pageSize })
      .then((result) => {
        if (cancelled) return;
        setFailed(false);
        setNodes(
          result
            ? mapServerGroupLevel(result.groups, groupBy, referenceData, depth, parentPath)
            : [],
        );
        setPagination(result?.pagination ?? undefined);
        setTotals(result?.totals ?? undefined);
      })
      .catch((error) => {
        if (cancelled) return;
        handleApiError(error, '/api/projects/grouped-tree', 'GET');
        setNodes([]);
        setPagination(undefined);
        setTotals(undefined);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, requestKey, page, pageSize, retryToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const changePageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  return {
    groups: nodes,
    pagination,
    totals,
    page,
    setPage,
    pageSize,
    setPageSize: changePageSize,
    loading,
    failed,
    retry,
  };
}
